import { useState, type FormEvent } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { api, message } from "../api";

type Step = "password" | "passkey";

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<Step>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ mfaRequired: boolean }>("/auth/login", { email, password });
      // The password step always leaves a session cookie; it just isn't
      // usable for anything until the passkey clears.
      if (res.mfaRequired) setStep("passkey");
      else onSignedIn();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitPasskey() {
    setBusy(true);
    setError(null);
    try {
      const options = await api.post<Parameters<typeof startAuthentication>[0]["optionsJSON"]>("/auth/passkey/authenticate/options");
      const assertion = await startAuthentication({ optionsJSON: options });
      await api.post("/auth/passkey/authenticate/verify", { response: assertion });
      onSignedIn();
    } catch (err) {
      // message() already reports a dismissed prompt as dismissed.
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <section className="auth__brand">
        <img
          className="auth__wordmark"
          src="/brand/logo-wordmark.png"
          alt="TimeSaver Offsite"
        />
        <p className="auth__tagline">
          Meeting minutes, drafted from the recording.
        </p>
        <p className="auth__since">Minnesota-based and family-run</p>
      </section>

      <section className="auth__panel">
        <div className="auth__form">
          {step === "password" ? (
            <form onSubmit={submitPassword} noValidate>
              <h1 className="auth__heading">Sign in</h1>
              <p className="auth__sub">Use your TimeSaver Offsite account.</p>

              {error && <div className="alert" role="alert">{error}</div>}

              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <button className="btn" type="submit" disabled={busy}>
                {busy ? "Checking…" : "Continue"}
              </button>
            </form>
          ) : (
            <div>
              <h1 className="auth__heading">Confirm it's you</h1>
              <p className="auth__sub">
                Your account is protected with a passkey. Approve the prompt to
                finish signing in.
              </p>

              {error && <div className="alert" role="alert">{error}</div>}

              <button className="btn" onClick={submitPasskey} disabled={busy}>
                {busy ? "Waiting for passkey…" : "Use passkey"}
              </button>

              <button
                className="btn btn--ghost"
                onClick={() => {
                  setStep("password");
                  setPassword("");
                  setError(null);
                }}
                disabled={busy}
              >
                Back
              </button>

              <p className="note">
                Lost access to your passkey? An administrator can reset it for
                you.
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
